import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { matchLegacyShopifyPath, isLegacyShopifyHost } from "@/lib/shopify/legacyStorefrontPaths";
import { logCheckoutClick } from "@/lib/checkoutClickLog";

/**
 * Detects when our SPA receives a known legacy Shopify storefront /
 * customer-account return path and redirects the customer back into the
 * canonical Zooi flow (order confirmation page or homepage).
 *
 * Also logs `wrong_host_landing` telemetry whenever:
 *  - the user arrived from a legacy `*.myshopify.com` / `shop.app` referrer,
 *    or
 *  - the current path matched a legacy rule.
 *
 * Mounts once at the App level. Skips /admin and /auth.
 */
const SKIP_PREFIXES = ["/admin", "/auth", "/api", "/.well-known"];

const LegacyShopifyRedirector = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = location.pathname;
    if (SKIP_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) return;

    const fullUrl = new URL(window.location.href);
    const match = matchLegacyShopifyPath(fullUrl);

    // Detect legacy referrer (customer clicked a Shopify-hosted CTA)
    let referrerHost = "";
    try {
      if (document.referrer) referrerHost = new URL(document.referrer).hostname;
    } catch {
      /* ignore */
    }
    const fromLegacyHost = referrerHost ? isLegacyShopifyHost(referrerHost) : false;

    if (match || fromLegacyHost) {
      logCheckoutClick({
        event: "checkout_fallback",
        meta: {
          kind: "wrong_host_landing",
          from_path: path,
          referrer_host: referrerHost || null,
          legacy_match_reason: match?.reason ?? null,
          target: match?.target ?? null,
          ts: new Date().toISOString(),
        },
      });
    }

    if (match) {
      navigate(match.target, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
};

export default LegacyShopifyRedirector;
